/**
 * Standing garrisons, and hosts inside the walls.
 *
 * Two of the owner's items, and they are one module because they meet at one
 * question: **what is standing inside a settlement when somebody attacks it.**
 *
 *   *"capital and main cities not villages should always have some small
 *   garrison and you can increase it with buildings and age"*
 *
 *   *"army can either fitht open field or you can choose army to hide in the
 *   walls, while selecting an army and if you are in a home settlement then let
 *   them hide, then you can take them out"*
 *
 * ── What was actually wrong ────────────────────────────────────────────────
 *
 * `ProvinceState.garrison` has existed since the strategic layer did, and there
 * is deliberately no second one: `src/sim/systems/garrison.ts` is the
 * *tactical* system — men with hit points standing on tiles of curtain in a
 * settlement world — and it stays exactly as it was. This is the strategic
 * half, where a garrison is a number of strength points on a province.
 *
 * The fault was that the number did not know **where it was**. `GARRISON_BASE`
 * is ninety for every one of six hundred and fifty provinces, so the seat of a
 * crown and a hamlet in the fens capped identically. Worse, nothing held a
 * floor under it: a bandit raid, a lost field battle or a month of siege could
 * take any holding on the board to five, and `doNextContact` has a rule that
 * says a province at five with no walls is simply *walked into*. So the largest
 * cities in the world could be, and routinely were, taken by whoever wandered
 * past next.
 *
 * ── The rule ───────────────────────────────────────────────────────────────
 *
 * A holding has a **rank**, which is a property of the *place* and not of
 * whoever currently holds it — see `holdingRank`. A seat of a crown is a seat
 * whoever's flag is over it; that is what makes a capital a prize worth taking
 * and a burden to hold, rather than a province that quietly becomes a village
 * the moment it changes hands.
 *
 * `standingGarrison` is what that rank keeps whatever else has happened, moved
 * by the two things the owner named: **buildings** (the tier ladder for every
 * crown, plus `ProvinceState.barracks` for the player's own city, which is a
 * real settlement with real barracks in it) and the **Age**. It is added to the
 * cap, and it is held as a floor by `doIncome` in any month nobody is sitting
 * outside the gate.
 *
 * **A siege still works, and that is the load-bearing part.** The floor is
 * applied in exactly one place — the monthly regrowth, which already skips a
 * besieged province — so an army that sits down in front of a capital grinds
 * the garrison to nothing exactly as it always did. What it can no longer do is
 * find the place empty when it gets there.
 *
 * ── And the host inside them ───────────────────────────────────────────────
 *
 * `Army.sheltered` is the second half. A host of your own, standing on your own
 * ground, in a holding with walls or a name, can be put **inside**. Sheltered:
 *
 *   - it is not found by the open-field contact scan, so it cannot be caught in
 *     the open — that is the whole of what the owner asked for;
 *   - it *is* the defence when somebody comes for the province, at the walls'
 *     own multiplier;
 *   - it cannot march, besiege, intercept or be intercepted until it is brought
 *     back out, which is the cost;
 *   - it is capped by `shelterCapacity`, so this is not a way to make a field
 *     army invulnerable between wars. A garrison is *of* its walls: a hamlet
 *     holds nobody and a great city holds a real force.
 *
 * The last two are why sheltering is not simply better than standing. A host
 * inside the walls has given up the board.
 */

import { MAX_AGE } from '../../data/ages.ts';
import { NATIONS, nationCapital } from '../../data/nations.ts';
import { armyPoints, rawPoints } from './armies.ts';
import { CHARTER_TIER, garrisonCapAt, isCity, tierGarrison, tierOf } from './holdings.ts';
import {
  ArmyStance,
  SHELTER_PER_GARRISON,
  STANDING_GARRISON_CITY,
  STANDING_GARRISON_SEAT,
  type Army,
  type CampaignState,
} from './types.ts';

/**
 * What a holding *is*, for the purpose of who keeps a watch.
 *
 * Three ranks and not four, because the owner's sentence names exactly three
 * things — capitals, main cities, and villages — and a rank the player cannot
 * name off the map is a rank that only exists in this file.
 */
export const HoldingRank = {
  /** Everything else. Keeps no standing garrison; it keeps a levy. */
  Village: 0,
  /** A chartered city: tier six, with a warehouse. */
  City: 1,
  /** A seat of a crown. Every nation's capital province in the data table. */
  Seat: 2,
} as const;
export type HoldingRankId = (typeof HoldingRank)[keyof typeof HoldingRank];

export const RANK_NAMES: Record<number, string> = {
  [HoldingRank.Village]: 'Village',
  [HoldingRank.City]: 'City',
  [HoldingRank.Seat]: 'Seat of a crown',
};

/**
 * Every province the data table names as somebody's capital.
 *
 * Built once, exactly as `bandits.ts` and `ai.ts` each build it, because this
 * is asked inside the monthly province loop over six hundred and fifty
 * provinces and a string lookup there is a measurable cost.
 */
const SEATS = new Set<number>(NATIONS.map((_, i) => nationCapital(i)));

/**
 * The rank of a holding — a property of the ground, not of the flag over it.
 *
 * This is the decision worth arguing with. Reading the rank off the *owner* —
 * "is this the capital of whoever holds it" — would mean that taking a great
 * city turns it into a village, and that a crown driven out of its seat carries
 * its walls with it. Neither is true of anywhere. A seat is a seat because
 * somebody spent three hundred years building it, and the conqueror inherits
 * both the men on the walls and the trouble of feeding them.
 */
export function holdingRank(c: CampaignState, p: number): number {
  if (SEATS.has(p)) return HoldingRank.Seat;
  /**
   * The tier check is not a shortcut, it is the whole reason this is affordable.
   *
   * `isCity` is a linear scan of the city list, and this is reached off
   * `garrisonCap`, which `ai.ts` calls inside its per-province scoring loop a
   * few million times a campaign. A charter is granted at `CHARTER_TIER` and
   * cannot be lost below it, so a holding under that tier is not a city and can
   * be answered without touching the list at all. Without this line the sweep
   * ran roughly a third longer and two test files timed out.
   *
   * The raw `tier` rather than `tierOf`, and that is the second half of it: the
   * clamp costs four `Math` calls, and for a `< CHARTER_TIER` comparison it
   * cannot change the answer — anything out of range is either below the
   * threshold already or clamps to a value on the same side of it. So the
   * common case, which is roughly nine provinces in ten, is one hash lookup and
   * one compare.
   */
  if ((c.provinces[p]?.tier ?? MIN_RANK_TIER) < CHARTER_TIER) return HoldingRank.Village;
  if (isCity(c, p)) return HoldingRank.City;
  return HoldingRank.Village;
}

/** What a province with no readable tier is treated as. Below any charter. */
const MIN_RANK_TIER = 1;

export function rankName(rank: number): string {
  return RANK_NAMES[rank] ?? 'Village';
}

/**
 * What an Age is worth to a watch, indexed by the Age being stood in.
 *
 * Index 0 is unreachable and exists only to keep the arithmetic honest, exactly
 * as every table in `ages.ts` does. It roughly doubles across the ladder, which
 * is the same shape `AGE_COST` climbs in: a Hollow-age capital is a fortress in
 * a way an Ember-age one is not, and the increase is felt by every crown at
 * once because an Age is when the board is, not who is winning it.
 */
export const AGE_GARRISON: readonly number[] = [1, 1, 1.18, 1.4, 1.66, 2];

export function ageGarrisonScale(age: number): number {
  const a = Math.max(1, Math.min(MAX_AGE, Math.round(age)));
  return AGE_GARRISON[a] ?? 1;
}

/**
 * The ceiling on what a city's own yards may be worth to its watch.
 *
 * A clamp rather than a hope. `barracks` arrives from a settlement world this
 * layer cannot see, through a command that crosses a wire in a room, and a
 * garbage value would scale every defence on the board. Set at roughly what a
 * seat is worth on its own, so a well-built capital roughly doubles its
 * standing garrison and nothing anybody can build ever triples it.
 */
export const MAX_BARRACKS_POINTS = 140;

/** The realm's Age, defaulting to the Ember for a campaign that has no idea. */
export function realmAge(c: CampaignState): number {
  return Math.max(1, Math.min(MAX_AGE, Math.round(c.age || 1)));
}

/**
 * The garrison this holding keeps whatever has happened to it.
 *
 * Zero on a village and on unclaimed ground, which is the rule stated as
 * arithmetic: *not villages*. Everywhere else it is the rank's own number,
 * scaled by the Age, plus what has been built here — the tier ladder, which is
 * every crown's buildings, and `barracks`, which is the player's real ones.
 */
export function standingGarrison(c: CampaignState, p: number): number {
  const prov = c.provinces[p];
  if (!prov || prov.owner < 0) return 0;
  const rank = holdingRank(c, p);
  // Nine provinces in ten leave here, having cost a hash lookup and a compare.
  // Everything below is arithmetic only a city or a seat ever pays for.
  if (rank === HoldingRank.Village) return 0;
  const base = rank === HoldingRank.Seat ? STANDING_GARRISON_SEAT : STANDING_GARRISON_CITY;
  // The works: the ladder for everybody, and the player's own yards on top.
  const works = tierGarrison(tierOf(c, p)) * 0.5 + Math.max(0, prov.barracks);
  return Math.round((base + works) * ageGarrisonScale(realmAge(c)));
}

// ── Inside the walls ───────────────────────────────────────────────────────

/** Hosts of the province's own crown, standing inside its walls. */
export function shelteredIn(c: CampaignState, province: number): Army[] {
  return c.armies.filter((a) => a.sheltered && a.at === province && a.aboard < 0 && a.moveTo < 0);
}

/** Points of host inside these walls right now. Raw, not fed and not veteran. */
export function shelteredPoints(c: CampaignState, province: number): number {
  let total = 0;
  for (const a of shelteredIn(c, province)) total += rawPoints(a);
  return total;
}

/**
 * The strongest host inside these walls that belongs to the crown holding them,
 * or undefined.
 *
 * The one the defence is fought by. Strongest rather than first because a
 * player who has put two hosts inside meant the walls to be held by both, and
 * the campaign's battle record carries exactly one defending army — see
 * `PendingBattle`. The rest are counted as `shelterSupport` and take their
 * share of the losses, which is the honest half of the compromise.
 */
export function shelterDefender(c: CampaignState, province: number): Army | undefined {
  let best: Army | undefined;
  const owner = c.provinces[province]?.owner ?? -1;
  for (const a of shelteredIn(c, province)) {
    if (a.owner !== owner) continue;
    if (a.regiments.length === 0) continue;
    if (!best || armyPoints(a) > armyPoints(best)) best = a;
  }
  return best;
}

/** Everything sheltered here except the one that will be named the defender. */
export function shelterSupport(c: CampaignState, province: number): Army[] {
  const lead = shelterDefender(c, province);
  const owner = c.provinces[province]?.owner ?? -1;
  return shelteredIn(c, province).filter(
    (a) => a.owner === owner && a !== lead && a.regiments.length > 0,
  );
}

/**
 * How much host these walls will take in, in raw points.
 *
 * Off the garrison cap, so it grows with everything the standing garrison grows
 * with — the walls, the ladder, the buildings, the Age — and so a hamlet is
 * genuinely no use as a bolt-hole. Zero on anything with neither walls nor a
 * name, which is what makes `canShelter` refuse a field.
 */
export function shelterCapacity(c: CampaignState, province: number): number {
  const prov = c.provinces[province];
  if (!prov || prov.owner < 0) return 0;
  if (prov.fort <= 0 && holdingRank(c, province) === HoldingRank.Village) return 0;
  return Math.round(garrisonCapAt(c, province) * SHELTER_PER_GARRISON);
}

/**
 * Why this host may not go inside, as a sentence, or `''`.
 *
 * The convention every campaign verb keeps: one predicate, one sentence, and
 * the UI greys the button from the same clause list the command enforces. A
 * refusal that only says "no" is a dead end.
 */
export function shelterRefusal(c: CampaignState, a: Army): string {
  if (a.sheltered) return 'They are already inside the walls.';
  if (a.aboard >= 0) return 'This host is aboard ship. It goes where the fleet goes.';
  if (a.moveTo >= 0) return 'This host is already on the road.';
  if (a.regiments.length === 0) return 'There is nobody left to put anywhere.';
  const prov = c.provinces[a.at];
  if (!prov) return 'Nowhere to shelter.';
  if (prov.owner !== a.owner) return 'A host may only take shelter behind its own walls.';
  const room = shelterCapacity(c, a.at);
  if (room <= 0) return 'There is nothing here to hide in. Walls, or a city, or neither.';
  const used = shelteredPoints(c, a.at);
  if (used + rawPoints(a) > room) {
    return `These walls will hold ${room} points and ${used} are already inside.`;
  }
  return '';
}

export function canShelter(c: CampaignState, a: Army): boolean {
  return shelterRefusal(c, a) === '';
}

/**
 * Put them inside. Returns false and changes nothing if it cannot.
 *
 * The stance goes back to `Field` because a host inside the walls is not
 * besieging anything, and `intercepting` is dropped for the same reason
 * `beginLeg` drops it: both are orders for the coming month and a host has one.
 */
export function shelterArmy(c: CampaignState, a: Army): boolean {
  if (!canShelter(c, a)) return false;
  a.sheltered = true;
  a.stance = ArmyStance.Field;
  a.siegeTurns = 0;
  a.intercepting = -1;
  a.idleTurns = 0;
  return true;
}

/** Bring them back out. Always legal — the walls are not a prison. */
export function sallyArmy(_c: CampaignState, a: Army): boolean {
  if (!a.sheltered) return false;
  a.sheltered = false;
  a.idleTurns = 0;
  return true;
}

/**
 * The walls have fallen with hosts inside them.
 *
 * Called from `captureProvince`, before the flag moves. They are not simply
 * captured — a host that surrenders wholesale with no fight is a rule players
 * will read as the game taking their army away — and they are not simply
 * released either, because then hiding would be free. They cut their way out,
 * and `SHELTER_BREAKOUT_SURVIVE` of them make it.
 *
 * The losses are applied by the caller, through the same `applyLosses` a lost
 * battle uses, so a breakout costs models out of the same ledger and by the
 * same arithmetic. This returns the hosts it turned back out rather than
 * killing them here: `applyLosses` and `removeArmy` both live in `armies.ts`,
 * which imports nothing from this file, and keeping that arrow pointing one way
 * is worth two lines at the call site.
 */
export const SHELTER_BREAKOUT_SURVIVE = 0.5;

export function breakShelter(c: CampaignState, province: number): Army[] {
  const out: Army[] = [];
  for (const a of shelteredIn(c, province)) {
    a.sheltered = false;
    a.supply = Math.min(a.supply, 0.4);
    out.push(a);
  }
  return out;
}
